<template>
  <div class="task-card">
    <div v-if="task.status !== 'created'" class="status" :class="colorClass">
      <v-icon :color="iconColor" size="15">{{ statusIcon }}</v-icon>
      <p class="mb-0 ml-2">{{ statusLabel }}</p>
    </div>
    <p class="description mb-0">{{ task.description }}</p>
    <p class="estimate mb-3">{{ `${formatTime(task.estimated_time)} ・ Estimativa ` }}</p>

    <div class="total-time is-flex mb-3">
      <p class="time" :class="{ 'pulsing-clock': task.status === 'working' }">{{ task.status === 'created' ? '-' : totalTime }}</p>
      <p class="info-description ml-2">Tempo total</p>
    </div>
    <template v-if="isExpanded">
      <div class="toggle-area">
        <div class="actions">
          <custom-checkbox v-model="isChecked" :disabled="task.status === 'created'" @update-status="toggleTaskFinished" :key="task.id" />
          <v-btn class="btn-start-pause" :class="colorClass" elevation="0" @click="startTaskAction">
            <v-icon v-if="task.status !== 'finished'" color="#fff">{{ btnIcon }}</v-icon>
            <p>{{ btnLabel }}</p>
          </v-btn>
          <v-btn class="btn-trash" color="#FFF5F5" elevation="0" @click="openModalDeleteTask">
            <v-icon color="#F56565">mdi-trash-can-outline</v-icon>
          </v-btn>
        </div>
        <div class="medias-intervals mt-2">
          <v-btn class="custom-btn" elevation="0" @click="changeScreen('task-intervals')">
            <v-icon size="18">mdi-timer-outline</v-icon>
            <p class="btn-text">Intervalos</p>
            <div class="btn-quantity">
              <p>{{ task.task_intervals.length }}</p>
            </div>
          </v-btn>
          <v-btn class="custom-btn" elevation="0" @click="changeScreen('task-medias')" :disabled="task.status === 'created' || disableActions">
            <v-icon size="18">mdi-image-multiple-outline</v-icon>
            <p class="btn-text">Mídias</p>
            <div class="btn-quantity">
              <p>99</p>
            </div>
          </v-btn>
        </div>
      </div>
    </template>

    <div class="toggle-actions mt-2" @click="toggleActions">
      <v-icon v-if="isExpanded" color="#4A5568">mdi-chevron-up</v-icon>
      <v-icon v-else color="#4A5568">mdi-chevron-down</v-icon>
    </div>
    <ModalDeleteTask :isActive="isModalDeleteTaskActive" :task="task" @confirm-delete="confirmDelete" @close="closeModalDeleteTask" />
  </div>
</template>

<script>
import CustomCheckbox from './CustomCheckbox';
import ModalDeleteTask from './ModalDeleteTask';

export default {
  name: 'TaskCard',
  components: { CustomCheckbox, ModalDeleteTask },
  props: {
    task: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isExpanded: false,
      isChecked: false,
      isModalDeleteTaskActive: false,
      btnLabels: {
        created: 'Iniciar',
        paused: 'Iniciar',
        working: 'Pausar',
        finished: 'Finalizado'
      },
      btnIcons: {
        created: 'mdi-play-circle-outline',
        paused: 'mdi-play-circle-outline',
        working: 'mdi-pause-circle-outline'
      },
      statusLabels: {
        paused: 'Em Pausa',
        working: 'Trabalhando',
        finished: 'Finalizada'
      },
      statusIcons: {
        paused: 'mdi-pause',
        working: 'mdi-wrench-outline',
        finished: 'mdi-check'
      },
      colorClasses: {
        created: 'theme-indigo',
        paused: 'theme-indigo',
        working: 'theme-orange',
        finished: 'theme-green'
      },
      iconColors: {
        created: '#434190',
        paused: '#434190',
        working: '#ED8936',
        finished: '#48BB78'
      }
    };
  },
  computed: {
    btnLabel() {
      return this.btnLabels[this.task.status];
    },
    btnIcon() {
      return this.btnIcons[this.task.status];
    },
    statusLabel() {
      return this.statusLabels[this.task.status];
    },
    statusIcon() {
      return this.statusIcons[this.task.status];
    },
    colorClass() {
      return this.colorClasses[this.task.status];
    },
    iconColor() {
      return this.iconColors[this.task.status];
    },
    disableActions() {
      return this.task.status === 'working';
    },
    taskIntervalActive() {
      return this.$store.getters['ServiceOrderTask/getServiceOrderTaskIntervalActive'];
    },
    taskIntervalActiveTotalTime() {
      return this.$store.getters['ServiceOrderTask/getTaskIntervalActiveTotalTime'];
    },
    totalTime() {
      return this.calculateTotalTime(this.task.task_intervals, this.taskIntervalActiveTotalTime);
    }
  },
  methods: {
    toggleActions() {
      this.isExpanded = !this.isExpanded;
    },
    openModalDeleteTask() {
      this.isModalDeleteTaskActive = true;
    },
    closeModalDeleteTask() {
      this.isModalDeleteTaskActive = false;
    },
    async confirmDelete() {
      this.$store.dispatch('ServiceOrderTask/deleteTask', this.task.id);
      this.isModalDeleteTaskActive = false;
    },
    changeScreen(screen) {
      if (screen === 'task-intervals') {
        this.$store.dispatch('ServiceOrderTask/setTaskToShowIntervalsHistory', this.task);
      }

      if (screen === 'task-medias') {
        this.$store.dispatch('ServiceOrderTask/setTaskToShowMedias', this.task);
      }

      this.$emit('change-screen', screen);
    },
    formatTime(time) {
      if (!time) return '--:--';
      const [hours, minutes] = time.split(':');
      return `${hours}:${minutes}`;
    },
    startTaskAction() {
      if ((this.task.status === 'created' || this.task.status === 'paused') && this.taskIntervalActive) {
        this.$toast.error('Uma tarefa já foi iniciada. Pause para iniciar outra.');
        return;
      }

      if (this.task.status === 'created' || this.task.status === 'paused') {
        const startDate = new Date();
        startDate.setSeconds(0, 0);

        this.$store.dispatch('ServiceOrderTask/createTaskInterval', {
          taskId: this.task.id,
          body: { service_order_task_id: this.task.id, start_date: startDate, status: 'working' }
        });
      }

      if (this.task.status === 'working') {
        const endDate = new Date();
        endDate.setSeconds(0, 0);

        this.$store.dispatch('ServiceOrderTask/finishTaskInterval', {
          taskId: this.task.id,
          intervalId: this.taskIntervalActive.id,
          body: { end_date: endDate }
        });

        this.$store.dispatch('ServiceOrderTask/stopIntervalTimer');
      }
    },
    async toggleTaskFinished() {
      if (this.task.status === 'working') {
        const endDate = new Date();
        endDate.setSeconds(0, 0);

        await this.$store.dispatch('ServiceOrderTask/finishTaskInterval', {
          taskId: this.task.id,
          intervalId: this.taskIntervalActive.id,
          body: { end_date: endDate }
        });

        await this.$store.dispatch('ServiceOrderTask/stopIntervalTimer');
      }

      const status = this.isChecked ? 'finished' : 'paused';
      this.$store.dispatch('ServiceOrderTask/updateTask', { taskId: this.task.id, body: { status } });
    },
    calculateTotalTime(intervals, taskIntervalActiveTotalTime) {
      let totalMilliseconds = 0;

      intervals.forEach((interval) => {
        if (interval?.id === this.taskIntervalActive?.id) {
          totalMilliseconds += taskIntervalActiveTotalTime;
        } else {
          const startDate = new Date(interval.start_date);
          const endDate = new Date(interval.end_date);
          startDate.setSeconds(0, 0);
          endDate.setSeconds(0, 0);
          totalMilliseconds += endDate - startDate;
        }
      });

      const totalMinutes = Math.floor(totalMilliseconds / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
  },
  mounted() {
    this.isChecked = this.task.status === 'finished';
  }
};
</script>

<style lang="scss" scoped>
@keyframes pulsing-clock {
  0% {
    opacity: 0;
  }
  70% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.pulsing-clock {
  animation: pulsing-clock 1.5s infinite;
}

p {
  margin: 0;
}

.task-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;

  .status {
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    padding: 4px 0;
    font-size: 14px;
    font-weight: 700;

    &.theme-indigo {
      background-color: #ebf4ff;
      color: #667eea;
    }

    &.theme-orange {
      background-color: #fffaf0;
      color: #ed8936;
    }

    &.theme-green {
      background-color: #f0fff4;
      color: #48bb78;
    }
  }

  .toggle-area {
    .actions {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .task-checkbox {
        height: 56px;
        color: #667eea;
      }

      .btn-start-pause {
        height: 56px;
        width: 60%;
        padding: 0;

        &.theme-indigo {
          background-color: #434190;
        }

        &.theme-orange {
          background-color: #ed8936;
        }

        &.theme-green {
          background-color: #48bb78;
        }

        p {
          color: #fff;
          font-weight: 600;
          font-size: 14px;
          margin-left: 6px;
        }
      }

      .btn-trash {
        height: 56px;
        min-width: 56px;
        padding: 0;
      }
    }
    .medias-intervals {
      display: flex;
      justify-content: space-between;
      .custom-btn {
        color: #4a5568;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        height: 40px;
        letter-spacing: 0.3px;
        text-transform: none;
        width: 48%;
        background-color: #fff;
        border: 1px solid #e2e8f0;
        height: 56px;

        .btn-text {
          margin-left: 6px;
        }

        .btn-quantity {
          margin-left: 6px;
          background-color: #f6f7f9;
          border-radius: 50%;
          display: flex;
          justify-content: center;
          align-items: center;
          height: 20px;
          width: 20px;
        }
      }
    }
  }

  .toggle-actions {
    align-items: center;
    background-color: #f6f7f9;
    border-radius: 6px;
    color: #667eea;
    display: flex;
    height: 28px;
    justify-content: center;
    padding: 4px 0;
  }

  .description {
    color: #4a5568;
    font-weight: 700;
    font-size: 16px;
    margin-top: 4px;
  }

  .estimate {
    color: #718096;
    font-weight: 400;
    font-size: 14px;
  }

  .total-time {
    align-items: baseline;
    display: flex;

    .time {
      color: #4a5568;
      font-weight: 700;
      font-size: 24px;
      line-height: 32px;
    }

    .info-description {
      color: #718096;
      font-weight: 400;
      font-size: 14px;
    }
  }
}
</style>
